import { useMemo } from "react";
import dayjs from "dayjs";
import { useGetMissionControlTenantSavingsQuery } from "src/store/api/generatedApi";

export interface SavingsDataPoint {
  value: number;
  label: string;
}

export const formatSavings = (value: number) => {
  return `$${value.toFixed(2)}/t`;
};

interface SavingsPoint {
  month: string;
  specific_savings: number;
  total_savings: number;
}

const aggregateSavings = (allSeries: SavingsPoint[][]): SavingsPoint[] => {
  const byMonth = new Map<
    string,
    {
      totalSavings: number;
      estimatedTons: number | null;
      fallbackSpecific: number;
    }
  >();

  allSeries.flat().forEach((point) => {
    const month = point.month;
    const totalSavings = point.total_savings;
    const specificSavings = point.specific_savings;
    const estimatedTons =
      specificSavings !== 0 ? totalSavings / specificSavings : null;
    const existing = byMonth.get(month);

    if (!existing) {
      byMonth.set(month, {
        totalSavings,
        estimatedTons,
        fallbackSpecific: specificSavings,
      });
      return;
    }

    byMonth.set(month, {
      totalSavings: existing.totalSavings + totalSavings,
      estimatedTons:
        existing.estimatedTons !== null || estimatedTons !== null
          ? (existing.estimatedTons ?? 0) + (estimatedTons ?? 0)
          : null,
      fallbackSpecific: existing.fallbackSpecific,
    });
  });

  return [...byMonth.entries()]
    .sort(([monthA], [monthB]) => monthA.localeCompare(monthB))
    .map(([month, values]) => ({
      month,
      total_savings: values.totalSavings,
      specific_savings:
        values.estimatedTons !== null && values.estimatedTons !== 0
          ? values.totalSavings / values.estimatedTons
          : values.fallbackSpecific,
    }));
};

export const useTenantSavings = (tenantNames: string[]) => {
  const primaryTenant = tenantNames[0] ?? "";
  const secondaryTenant = tenantNames[1] ?? "";

  const { data: primarySavingsData, isLoading: primaryLoading } =
    useGetMissionControlTenantSavingsQuery(
      { tenantName: primaryTenant },
      { skip: primaryTenant.length === 0 }
    );
  const { data: secondarySavingsData, isLoading: secondaryLoading } =
    useGetMissionControlTenantSavingsQuery(
      { tenantName: secondaryTenant },
      { skip: secondaryTenant.length === 0 }
    );

  const isLoading = primaryLoading || secondaryLoading;

  const savings = useMemo(() => {
    const series = [
      primarySavingsData?.savings ?? [],
      secondarySavingsData?.savings ?? [],
    ].filter((points) => points.length > 0) as SavingsPoint[][];
    return aggregateSavings(series);
  }, [primarySavingsData, secondarySavingsData]);

  const hasSavingsData = savings.length > 0;

  const latestSavings = useMemo(() => {
    if (!hasSavingsData) return null;
    return savings[savings.length - 1];
  }, [hasSavingsData, savings]);

  const savingsDataPoints = useMemo((): SavingsDataPoint[] => {
    if (!hasSavingsData) return [];
    return savings.map((s) => ({
      value: s.specific_savings,
      label: dayjs(s.month).format("MMM YYYY"),
    }));
  }, [hasSavingsData, savings]);

  const formattedMonth = useMemo(() => {
    if (!latestSavings) return "";
    return dayjs(latestSavings.month).format("MMM YYYY");
  }, [latestSavings]);

  return {
    isLoading,
    hasSavingsData,
    latestSavings,
    savingsDataPoints,
    formattedMonth,
  };
};
